import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import PlanFilter from '../../components/common/PlanFilter';

const NewPlan = ({ open, onClose, onSave, plan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    plan_type: '',
    is_active: true,
    version: 1,
    per_user_cost: 0,
    per_credit_cost: 0,
    total_credits: 0,
    expiry_days: 30,
    insights_allowed: false,
    max_user_slots: 5
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Plan type options for PlanFilter
  const planTypeOptions = [
    { value: 'free', label: 'Free', description: 'Basic plan with limited features' },
    { value: 'basic', label: 'Basic', description: 'Standard plan for growing businesses' },
    { value: 'advance', label: 'Advance', description: 'Premium plan for enterprise needs' }
  ];

  // Reset form when modal opens/closes or plan changes
  useEffect(() => {
    if (open) {
      setErrors({});
      if (plan) {
        // Edit mode
        setFormData({
          name: plan.name || '',
          plan_type: plan.plan_type || '',
          is_active: plan.is_active !== undefined ? plan.is_active : true,
          version: plan.version || 1,
          per_user_cost: plan.per_user_cost || 0,
          per_credit_cost: plan.per_credit_cost || 0,
          total_credits: plan.total_credits || 0,
          expiry_days: plan.expiry_days || 30,
          insights_allowed: plan.insights_allowed || false,
          max_user_slots: plan.max_user_slots || 5
        });
      } else {
        // Create mode
        setFormData({
          name: '',
          plan_type: '',
          is_active: true,
          version: 1,
          per_user_cost: 0,
          per_credit_cost: 0,
          total_credits: 0,
          expiry_days: 30,
          insights_allowed: false,
          max_user_slots: 5
        });
      }
    }
  }, [open, plan]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    }
    if (!formData.plan_type) {
      newErrors.plan_type = 'Plan type is required';
    }
    if (formData.per_user_cost < 0) {
      newErrors.per_user_cost = 'Per user cost cannot be negative';
    }
    if (formData.per_credit_cost < 0) {
      newErrors.per_credit_cost = 'Per credit cost cannot be negative';
    }
    if (formData.total_credits < 0) {
      newErrors.total_credits = 'Total credits cannot be negative';
    }
    if (formData.expiry_days < 1) {
      newErrors.expiry_days = 'Expiry days must be at least 1';
    }
    if (formData.max_user_slots < 1) {
      newErrors.max_user_slots = 'Max user slots must be at least 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const planData = {
        ...formData,
        per_user_cost: parseFloat(formData.per_user_cost),
        per_credit_cost: parseInt(formData.per_credit_cost),
        total_credits: parseInt(formData.total_credits),
        expiry_days: parseInt(formData.expiry_days),
        max_user_slots: parseInt(formData.max_user_slots)
      };

      await onSave(planData);
      setSnackbar({
        open: true,
        message: plan ? 'Plan updated successfully' : 'Plan created successfully',
        severity: 'success'
      });
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error saving plan',
        severity: 'error'
      });
    }
  };

  const getPlanTypeInfo = (type) => {
    return planTypeOptions.find(option => option.value === type);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold">
              {plan ? 'Edit Plan' : 'Create New Plan'}
            </Typography>
          </Box>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 3 }}>
          {/* Main Grid Layout */}
          <Grid container spacing={3} alignItems="flex-start">
            
            {/* LEFT COLUMN - Details/Form */}
            <Grid item xs={12} md={7}>
              {/* Basic Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <BusinessIcon color="primary" />
                    Basic Information
                  </Typography>
                  <Grid container spacing={3}>
                    {/* Plan Name */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Plan Name *"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name || 'Enter a descriptive name for the plan'}
                      />
                    </Grid>
                    {/* Plan Type */}
                    <Grid item xs={12} md={6}>
                      <PlanFilter
                        value={formData.plan_type}
                        onChange={(value) => handleInputChange('plan_type', value)}
                        label="Plan Type *"
                        fullWidth={true}
                      />
                      {errors.plan_type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          {errors.plan_type}
                        </Typography>
                      )}
                    </Grid>

                    {/* Version */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Version"
                        type="number"
                        value={formData.version}
                        onChange={(e) => handleInputChange('version', parseInt(e.target.value) || 1)}
                        fullWidth
                        inputProps={{ min: 1 }}
                        helperText="Plan version number"
                      />
                    </Grid>
                    {/* Active Toggle */}
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={<Switch checked={formData.is_active} onChange={(e) => handleInputChange('is_active', e.target.checked)} color="primary" />}
                        label="Active Plan"
                        sx={{ mt: 2 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Pricing */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CreditCardIcon color="primary" />
                    Pricing Configuration
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Per User Cost"
                        type="number"
                        value={formData.per_user_cost}
                        onChange={(e) => handleInputChange('per_user_cost', parseFloat(e.target.value) || 0)}
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        error={!!errors.per_user_cost}
                        helperText={errors.per_user_cost || 'Cost per user per billing cycle'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Per Credit Cost"
                        type="number"
                        value={formData.per_credit_cost}
                        onChange={(e) => handleInputChange('per_credit_cost', parseInt(e.target.value) || 0)}
                        fullWidth
                        inputProps={{ min: 0 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                        error={!!errors.per_credit_cost}
                        helperText={errors.per_credit_cost || 'Cost per credit for insights'}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Limits */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <PeopleIcon color="primary" />
                    Limits & Features
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Credits"
                        type="number"
                        value={formData.total_credits}
                        onChange={(e) => handleInputChange('total_credits', parseInt(e.target.value) || 0)}
                        fullWidth
                        error={!!errors.total_credits}
                        helperText={errors.total_credits || 'Total credits allowed'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Max User Slots"
                        type="number"
                        value={formData.max_user_slots}
                        onChange={(e) => handleInputChange('max_user_slots', parseInt(e.target.value) || 1)}
                        fullWidth
                        error={!!errors.max_user_slots}
                        helperText={errors.max_user_slots || 'Maximum users allowed'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Expiry Days"
                        type="number"
                        value={formData.expiry_days}
                        onChange={(e) => handleInputChange('expiry_days', parseInt(e.target.value) || 30)}
                        fullWidth
                        error={!!errors.expiry_days}
                        helperText={errors.expiry_days || 'Plan expiry duration'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={<Switch checked={formData.insights_allowed} onChange={(e) => handleInputChange('insights_allowed', e.target.checked)} color="primary" />}
                        label="Insights Allowed"
                        sx={{ mt: 2 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* RIGHT COLUMN - Summary */}
            <Grid item xs={12} md={5}>
              <Card sx={{ position: 'sticky', top: 16 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CheckCircleIcon color="primary" />
                    Plan Summary
                  </Typography>

                  {/* Enhanced Summary with better layout */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Plan Overview */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Plan Overview
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Plan Name:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {formData.name || 'Not set'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Plan Type:</Typography>
                          <Chip 
                            label={getPlanTypeInfo(formData.plan_type)?.label || 'Not selected'} 
                            size="small" 
                            color={formData.plan_type === 'free' ? 'default' : formData.plan_type === 'basic' ? 'primary' : 'secondary'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Version:</Typography>
                          <Typography variant="body2" fontWeight="bold">v{formData.version}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Pricing Summary */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Pricing Structure
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Per User Cost:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ₹{formData.per_user_cost || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Per Credit Cost:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ₹{formData.per_credit_cost || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Limits & Features */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Limits & Features
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Total Credits:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formData.total_credits.toLocaleString() || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Max Users:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formData.max_user_slots || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Expiry Days:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formData.expiry_days || 0} days
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Status & Features */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Status & Features
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Status:</Typography>
                          <Chip 
                            label={formData.is_active ? 'Active' : 'Inactive'} 
                            color={formData.is_active ? 'success' : 'error'} 
                            size="small" 
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">Insights:</Typography>
                          <Chip 
                            label={formData.insights_allowed ? 'Allowed' : 'Not Allowed'} 
                            color={formData.insights_allowed ? 'success' : 'default'} 
                            size="small" 
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Quick Stats */}
                    {formData.name && formData.plan_type && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Quick Stats
                          </Typography>
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: 1.5,
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1
                          }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" fontWeight="bold" color="primary">
                                ₹{(formData.per_user_cost * formData.max_user_slots).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Max Revenue/User
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" fontWeight="bold" color="secondary">
                                ₹{(formData.per_credit_cost * formData.total_credits).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Total Credit Value
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>


        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0', 
          pt: 2, 
          px: 3, 
          pb: 3,
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              {plan ? `Editing Plan: ${plan.name}` : 'Creating new subscription plan'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {plan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewPlan; 